import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

import { RequestAccessDialog } from './RequestAccessDialog'

/**
 * A refusal, said plainly, with the one thing that can change it.
 *
 * No Retry: a 403 is the service's answer about this person, and asking
 * again gives the same one. What can change it is somebody giving access —
 * so the action is **Request access**, which asks VentaWiz Auth on the
 * reader's behalf. The service's own words, when it gave some ("Needs
 * ads:view"), are kept under the sentence: they say what to ask for.
 */
export function ForbiddenState({
  reason = 'general',
  detail,
}: {
  reason?: 'page' | 'general'
  detail?: string | undefined
}) {
  const { t } = useTranslation()
  // A dialog's open state holds an unsaved draft: not view state.
  const [asking, setAsking] = useState(false)

  return (
    <div
      role="status"
      className="bg-card flex flex-col items-center gap-3 rounded-lg border px-8 py-12 text-center shadow-xs"
    >
      <Icon name="shield_lock" size={48} className="text-foreground-disabled" />
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{t('forbidden.title')}</h3>
        <p className="text-foreground-secondary max-w-95 text-sm">{t(`forbidden.${reason}`)}</p>
        {detail ? <p className="text-muted-foreground max-w-95 text-xs">{detail}</p> : null}
      </div>
      <Button variant="outline" className="mt-1" onClick={() => setAsking(true)}>
        <Icon name="key" size={16} />
        {t('forbidden.request')}
      </Button>
      {asking ? <RequestAccessDialog onClose={() => setAsking(false)} /> : null}
    </div>
  )
}
